import { api } from '@common';
import { useQuery, useMutation } from '@tanstack/react-query';
import { message } from 'antd';

// Interface for Order Item
export interface OrderItem {
  id: string;
  productId: string;
  productName: string;
  sku?: string;
  variantId?: string;
  size?: string;
  color?: string;
  quantity: number;
  price: number;
  subtotal: number;
  createdAt: Date;
}

// Interface for Order
export interface Order {
  id: string;
  orderNumber: string;
  customerEmail: string;
  status: string;
  totalAmount: number;
  subtotalBeforeDiscount: number;
  discountAmount: number;
  currency: string;
  createdAt: Date;
  updatedAt: Date;
  items: OrderItem[];
}

// Interface for creating checkout session
interface LineItem {
  productId?: string; // Optional - for existing products
  name?: string; // For custom products
  description?: string; // For custom products
  price?: number; // For custom products (in dollars)
  quantity: number;
}

interface CreateCheckoutSessionInput {
  // Legacy single product support
  productId?: string;
  quantity?: number;
  // New flexible line items support
  lineItems?: LineItem[];
  email?: string;
  successUrl?: string;
  cancelUrl?: string;
}

interface CheckoutSessionResponse {
  url: string;
}

interface UseOrdersOptions {
  enableOrdersFetch?: boolean;
}

export const useOrders = (options?: UseOrdersOptions) => {
  const { enableOrdersFetch = false } = options || {};
  
  // Fetch all orders for authenticated user
  const orders = useQuery<Order[]>({
    queryKey: ['orders'],
    queryFn: async () => {
      try {
        const response = await api.get('/orders');
        return response.data;
      } catch (error) {
        console.error('Error fetching orders:', error);
        message.error('Error loading orders');
        throw error;
      }
    },
    enabled: enableOrdersFetch && !!localStorage.getItem('token'),
  });

  // Fetch a single order by ID
  const getOrder = (orderId: string, accessToken?: string) => {
    return useQuery<Order>({
      queryKey: ['order', orderId, accessToken],
      queryFn: async () => {
        try {
          const params = accessToken ? { accessToken } : {};
          const response = await api.get(`/orders/${orderId}`, { params });
          return response.data;
        } catch (error) {
          console.error(`Error fetching order ${orderId}:`, error);
          message.error('Error loading order details');
          throw error;
        }
      },
      enabled: !!orderId,
    });
  };

  // Fetch order by Stripe session ID (for order confirmation page)
  const getOrderBySessionId = (sessionId: string) => {
    return useQuery<Order>({
      queryKey: ['order', 'session', sessionId],
      queryFn: async () => {
        try {
          const response = await api.get(`/orders/session/${sessionId}`);
          return response.data;
        } catch (error: any) {
          // Don't log 404 as an error - it's expected when order hasn't been created yet
          if (error?.response?.status !== 404) {
            console.error(`Error fetching order by session ${sessionId}:`, error);
          }
          throw error;
        }
      },
      enabled: !!sessionId && sessionId.startsWith('cs_'),
      retry: (failureCount, error: any) => {
        // Retry up to 5 times for 404 (order may not be created yet after webhook)
        if (error?.response?.status === 404 && failureCount < 5) {
          return true;
        }
        // For other errors, retry up to 3 times
        return failureCount < 3;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * (attemptIndex + 1), 5000),
    });
  };

  // Create a checkout session for payment
  const createCheckoutSession = useMutation({
    mutationFn: async (data: CreateCheckoutSessionInput): Promise<CheckoutSessionResponse> => {
      try {
        const response = await api.post('/payments/create-checkout-session', data);
        return response.data;
      } catch (error: any) {
        console.error('Error creating checkout session:', error);
        
        if (error.response?.data?.message) {
          const errorMsg = Array.isArray(error.response.data.message)
            ? error.response.data.message.join(', ')
            : error.response.data.message;
          message.error(`Unable to create checkout session: ${errorMsg}`);
        } else {
          message.error('Unable to create checkout session');
        }
        
        throw error;
      }
    },
    onSuccess: (data) => {
      // Redirect to Stripe checkout
      console.log('Checkout session response:', data);
      console.log('Stripe URL:', data.url);
      if (data.url) {
        console.log('Redirecting to:', data.url);
        window.location.href = data.url;
      } else {
        console.error('No URL in response!');
      }
    }
  });

  // Create a portal session for subscription management
  const createPortalSession = useMutation({
    mutationFn: async (returnUrl?: string): Promise<CheckoutSessionResponse> => {
      try {
        const response = await api.post('/payments/create-portal-session', {
          returnUrl,
        });
        return response.data;
      } catch (error) {
        console.error('Error creating portal session:', error);
        message.error('Unable to create portal session');
        throw error;
      }
    },
    onSuccess: (data) => {
      // Redirect to Stripe portal
      if (data.url) {
        window.location.href = data.url;
      }
    }
  });

  // Cancel an order
  const cancelOrder = useMutation({
    mutationFn: async (orderId: string): Promise<Order> => {
      try {
        const response = await api.patch(`/orders/${orderId}/cancel`);
        return response.data;
      } catch (error: any) {
        console.error('Error cancelling order:', error);
        
        if (error.response?.data?.message) {
          const errorMsg = Array.isArray(error.response.data.message)
            ? error.response.data.message.join(', ')
            : error.response.data.message;
          message.error(`Unable to cancel order: ${errorMsg}`);
        } else {
          message.error('Unable to cancel order');
        }
        
        throw error;
      }
    },
    onSuccess: () => {
      message.success('Order cancelled successfully');
      // Invalidate orders query to refetch the list
      orders.refetch();
    }
  });

  return {
    orders,
    getOrder,
    getOrderBySessionId,
    createCheckoutSession,
    createPortalSession,
    cancelOrder,
  };
};

